//import logo from "./logo.svg";
import "./App.css";
import "bootstrap/dist/css/bootstrap.min.css";

import { React, useContext, useEffect } from "react";
import { Routes, Route, useLocation, useNavigate } from "react-router-dom";
import ReactGA from "react-ga4";

import { AuthContext } from "./shared/context/auth-context";
import { TourProvider } from "./shared/context/TourContext";
import { UpdateProvider } from "./shared/context/UpdateContext";
import SuperAdminRoute from "./shared/context/SuperAdminRoute";
import OrgAdminRoute from "./shared/context/OrgAdminRoute";
import Dashboard from "./base/pages/Dashboard";
import Auth from "./user/pages/Auth";
import Settings from "./user/pages/Settings";

import TopNavbar from "./base/components/TopNavbar";

import ClientList from "./workspace/components/ClientList";
import ScriptList from "./workspace/components/ScriptList";
import ScriptEditor from "./workspace/components/ScriptEditor";
import VideoList from "./workspace/components/VideoList";
import VideoEditor from "./workspace/components/VideoEditor";
import AvatarViewer from "./workspace/pages/AvatarViewer";
import AdminSettings from "./user/pages/AdminSettings";
import OrgSettings from "./user/pages/OrgSettings";
import NewOrg from "./user/components/NewOrg";
import NotInOrganization from "./components/NotInOrganization";
import AdminAddAvatar from "./heygen/pages/AdminAddAvatar";
import InvitePage from "./user/pages/InvitePage";
import ForgotPassword from "./user/pages/ForgotPassword";
import VideoPlayer from "./user/pages/VideoPlayer";
import PublicVideoPlayer from "./components/PublicVideoPlayer";
import UserListAdmin from "./user/pages/UserListAdmin";
import AuthAction from "./user/pages/AuthAction";
import MfaSetup from "./user/pages/MfaSetup";
import DrivePlayer from "./components/DrivePlayer";
import Audios from "./workspace/pages/Audios";
import Folders from "./workspace/pages/Folders";
import FolderFiles from "./workspace/components/FolderFiles";
import Tickets from "./workspace/pages/Tickets";
import TicketEditor from "./workspace/components/TicketEditor";
import StartNewVideo from "./base/pages/StartNewVideo";
import InteractivePage from "./interactive/pages/InteractivePage";
import Stripe from "./user/pages/Stripe";
import SubscriptionDashboard from "./subscription/pages/SubscriptionDashboard";
import TeamManagement from "./subscription/pages/TeamManagement";

function App() {
	const {
		isLoggedIn,
		isAuthReady,
		isSuperAdmin,
		isInOrganization,
		hasPhoneMfa,
		requiresMFA,
	} = useContext(AuthContext);
	const location = useLocation();
	const navigate = useNavigate();

	useEffect(() => {
		ReactGA.send({ hitType: "pageview", page: location.pathname });
	}, [location]);

	useEffect(() => {
		if (!isAuthReady) return;
		if (
			!isLoggedIn &&
			!(
				location.pathname === "/" ||
				location.pathname === "/invite" ||
				location.pathname === "/forgot-password" ||
				location.pathname === "/video" ||
				location.pathname === "/auth/action" ||
				location.pathname.startsWith("/share") ||
				location.pathname.startsWith("/drive")
			)
		) {
			navigate("/");
		}
	}, [isLoggedIn, navigate, location.pathname, isAuthReady]);

	//Auth delay for route rendering
	if (!isAuthReady) {
		return (
			<div
				style={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					height: "100vh",
					width: "100vw",
				}}
			>
				<img
					src="/R-gradient.png"
					alt="loading..."
					style={{
						width: "100px",
						height: "auto",
						animation: "pulseAndFade 1.5s ease-in-out infinite",
					}}
				/>
			</div>
		);
	}

	let routes;

	if (isLoggedIn) {
		const isOnSharePage = location.pathname.startsWith("/share");

		if (!isSuperAdmin && !isInOrganization) {
			return (
				<main>
					<NotInOrganization />
				</main>
			);
		}

		if (requiresMFA && !hasPhoneMfa && location.pathname !== "/auth/action") {
			console.log(requiresMFA);
			return (
				<main>
					<MfaSetup />
				</main>
			);
		}

		routes = (
			<div className="app-layout">
				{!isOnSharePage && <TopNavbar />}
				<div className="app-content mt-5 pt-4">
					<Routes>
						<Route path="/" element={<Dashboard />} />
						{/*<Route path="/avatarconfig" element={<HeygenConfig />} />*/}
						<Route path="/avatarlist" element={<AvatarViewer />} />
						<Route path="/add-avatar" element={<OrgAdminRoute />}>
							<Route index element={<AdminAddAvatar />} />
						</Route>
						<Route path="/clients" element={<ClientList />} />
						<Route path="/settings" element={<Settings />} />
						<Route path="/admin" element={<SuperAdminRoute />}>
							<Route index element={<AdminSettings />} />
						</Route>
						<Route path="/organizations/:orgId" element={<SuperAdminRoute />}>
							<Route index element={<OrgSettings />} />
						</Route>
						<Route path="/organizations/new" element={<SuperAdminRoute />}>
							<Route index element={<NewOrg />} />
						</Route>
						<Route path="/user-list" element={<SuperAdminRoute />}>
							<Route index element={<UserListAdmin />} />
						</Route>
						<Route path="/scripts" element={<ScriptList />} />
						<Route path="/scripts/new" element={<ScriptEditor />} />
						<Route path="/scripts/edit/:scriptId" element={<ScriptEditor />} />
						{/*<Route path="/campaigns" element={<CampaignList />} />*/}
						<Route path="/videos" element={<VideoList />} />
						<Route path="/start-new-video" element={<StartNewVideo />} />
						<Route path="/videos/new" element={<VideoEditor />} />
						<Route path="/videos/edit/:videoId" element={<VideoEditor />} />
						<Route path="/video" element={<VideoPlayer />} />
						<Route path="/share/:videoId" element={<PublicVideoPlayer />} />
						<Route path="/auth/action" element={<AuthAction />} />
						<Route path="/drive/:driveId" element={<DrivePlayer />} />
						<Route path="/folders" element={<Folders />} />
						<Route path="/folders/:folderId" element={<FolderFiles />} />
						<Route path="/audios" element={<Audios />} />
						<Route path="/tickets" element={<Tickets />} />
						<Route path="/tickets/new" element={<TicketEditor />} />
						<Route path="/tickets/:ticketId" element={<TicketEditor />} />
						<Route path="/interactive" element={<InteractivePage />} />
						<Route path="/stripe" element={<Stripe />} />
						<Route path="/subscription" element={<SubscriptionDashboard />} />
						<Route path="/team" element={<TeamManagement />} />
					</Routes>
				</div>
			</div>
		);
	} else {
		routes = (
			<div className="app-layout">
				<Routes>
					<Route path="/" element={<Auth />} />
					<Route path="/invite" element={<InvitePage />} />
					<Route path="/forgot-password" element={<ForgotPassword />} />
					<Route path="/video" element={<VideoPlayer />} />
					<Route path="/share/:videoId" element={<PublicVideoPlayer />} />
					<Route path="/auth/action" element={<AuthAction />} />
					<Route path="/drive/:driveId" element={<DrivePlayer />} />
				</Routes>
			</div>
		);
	}

	return (
		<>
			<main>
				{isLoggedIn && (isSuperAdmin || isInOrganization) ? (
					<UpdateProvider>
						<TourProvider>{routes}</TourProvider>
					</UpdateProvider>
				) : (
					routes
				)}
			</main>
		</>
	);
}

export default App;
